<template>
  <PopoverView v-model:active="active" placement="bottom" class="d-inline-block">
    <template v-slot:target>
      <slot name="target" :toggle="() => active = !active"></slot>
    </template>
    <template v-slot:body>
      <div class="date-picker">
        <div v-if="selecting === 'day'" class="date-picker-header">
          <button class="date-picker-button" @click="previousMonthClicked()" :disabled="minDate && date.minus({ months: 1 }).endOf('month').diff(minDate) < 0"><i class="fas fa-arrow-left"></i></button>
          <button class="date-picker-button" @click.stop="selecting = 'month'">{{ months[date.month - 1] }} {{ date.year}}</button>
          <button class="date-picker-button" @click="nextMonthClicked()" :disabled="maxDate && date.plus({ months: 1 }).startOf('month').diff(maxDate) > 0"><i class="fas fa-arrow-right"></i></button>
        </div>
        <div v-else-if="selecting === 'month'" class="date-picker-header">
          <button class="date-picker-button" @click="date = date.minus({ years: 1 })" :disabled="minDate && date.minus({ years: 1 }).year < minDate.year"><i class="fas fa-arrow-left"></i></button>
          <button class="date-picker-button" @click.stop="selecting = 'year'">{{ date.year }}</button>
          <button class="date-picker-button" @click="date = date.plus({ years: 1 })" :disabled="maxDate && date.plus({ years: 1 }).year > maxDate.year"><i class="fas fa-arrow-right"></i></button>
        </div>
        <div v-else-if="selecting === 'year'" class="date-picker-header">
          <button class="date-picker-button" @click="date = date.minus({ years: 1 })" :disabled="minDate && date.minus({ years: 1 }).year < minDate.year"><i class="fas fa-arrow-left"></i></button>
          <button class="date-picker-button" @click.stop="selecting = 'year'">{{ date.year }}</button>
          <button class="date-picker-button" @click="date = date.plus({ years: 1 })" :disabled="maxDate && date.plus({ years: 1 }).year > maxDate.year"><i class="fas fa-arrow-right"></i></button>
        </div>
        <div v-if="selecting === 'day'" class="date-picker-body">
          <div class="date-picker-row">
            <span class="date-picker-column date-picker-weekday" v-for="day in weekDays" :key="day">
              {{ day }}
            </span>
          </div>
          <div class="date-picker-row" v-for="(week, index) in weeks" :key="index">
            <div class="date-picker-column" v-for="(day, index) in week" :key="index">
              <button class="date-picker-button" :class="{ 'text-muted': day.month !== date.month, 'active': date.startOf('day').equals(day.startOf('day')) }" :disabled="minDate && day.endOf('day').diff(minDate) < 0 || maxDate && day.startOf('day').diff(maxDate) > 0" @click.stop="dateSelected(day)">{{ day.day }}</button>
            </div>
          </div>
        </div>
        <div v-else-if="selecting === 'month'" class="date-picker-body">
          <div class="date-picker-row" v-for="(group, index) in [[1, 2, 3], [4, 5, 6], [7, 8, 9], [10, 11, 12]]" :key="index">
            <div class="date-picker-column" v-for="month in group" :key="month">
              <button class="date-picker-button" :class="{ 'active': date.month === month }" @click.stop="monthSelected(month)" :disabled="minDate && date.set({ month: month }).endOf('month').diff(minDate) < 0 || maxDate && date.set({ month: month }).startOf('month').diff(maxDate) > 0">{{ months[month - 1] }}</button>
            </div>
          </div>
        </div>
        <div v-else-if="selecting === 'year'" class="date-picker-body">
          <div class="date-picker-row" v-for="(group, index) in [[-4, -3, -2], [-1, 0, 1], [2, 3, 4]]" :key="index">
            <div class="date-picker-column" v-for="year in group" :key="year">
              <button class="date-picker-button" :class="{ 'active': year === 0 }" @click.stop="yearSelected(date.year + year)" :disabled="minDate && (date.year + year) < minDate.year || maxDate && (date.year + year) > maxDate.year">{{ date.year + year }}</button>
            </div>
          </div>
        </div>
        <div v-if="time && selecting === 'day'" class="mt-3">
          <div class="d-flex align-items-center justify-content-center g-2">
            <InputView type="text" :value="String(date.hour)" @update:value="parseInt($event) !== NaN && parseInt($event) >= 0 && parseInt($event) <= 23 && (!minDate || date.set({ hour: parseInt($event) }).endOf('hour').diff(minDate) >= 0 && !maxDate || date.set({ hour: parseInt($event) }).startOf('hour').diff(maxDate) <= 0) ? date = date.set({ hour: parseInt($event) }) : undefined" style="max-width: calc(1em * 4)"/>
            <span class="font-weight-bold">:</span>
            <InputView type="text" :value="String(date.minute)" @update:value="parseInt($event) !== NaN && parseInt($event) >= 0 && parseInt($event) <= 59 && (!minDate || date.set({ minute: parseInt($event) }).diff(minDate) >= 0 && !maxDate || date.set({ minute: parseInt($event) }).diff(maxDate) <= 0) ? date = date.set({ minute: parseInt($event) }) : undefined" style="max-width: calc(1em * 4)"/>
            <span class="font-weight-bold">:</span>
            <InputView type="text" :value="String(date.second)" @update:value="parseInt($event) !== NaN && parseInt($event) >= 0 && parseInt($event) <= 59 && (!minDate || date.set({ second: parseInt($event) }).diff(minDate) >= 0 && !maxDate || date.set({ second: parseInt($event) }).diff(maxDate) <= 0) ? date = date.set({ second: parseInt($event) }) : undefined" style="max-width: calc(1em * 4)"/>
          </div>
        </div>
      </div>
    </template>
  </PopoverView>
</template>

<script>
import {DateTime, Info} from "luxon";
import PopoverView from "../../general/PopoverView.vue";
import InputView from "../../forms/inputs/InputView.vue";

export default {
  name: "DatePickerView",
  components: {PopoverView, InputView},
  emits: ["update:value"],
  props: {
    title: String,
    time: Boolean,
    value: String,
    required: Boolean,
    disabled: Boolean,
    min: String,
    max: String,
    size: {
      type: String,
      validator(value) {
        return ["small", "medium", "large"].includes(value);
      }
    },
    errors: Array,
    locale: String
  },
  data() {
    return {
      date: undefined,
      selecting: "day",
      minDate: undefined,
      maxDate: undefined,
      yearField: undefined,
      active: false
    }
  },
  mounted() {
    if (!this.date) this.date = DateTime.utc().setZone("system");
    if (!this.time) this.date = this.date.startOf("day");
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        if (this.value) {
          if (this.time) {
            this.date = DateTime.fromISO(this.value, { zone: "utc" }).setZone("system");
          } else {
            this.date = DateTime.fromISO(this.value, { zone: "utc" }).setZone("system");
          }
        } else {
          this.date = undefined;
        }
      }
    },
    min: {
      immediate: true,
      handler() {
        this.minDate = this.min ? DateTime.fromISO(this.min, { zone: "utc" }).setZone("system") : undefined;
      }
    },
    max: {
      immediate: true,
      handler() {
        this.maxDate = this.max ? DateTime.fromISO(this.max, { zone: "utc" }).setZone("system") : undefined;
      }
    },
    date() {
      this.checkValidity();
      this.yearField = this.date.year;
      this.$emit("update:value", this.time ? this.date.toUTC().toISO() : this.date.startOf("day").toUTC().toISO());
    },
    active() {
      if (!this.active) {
        this.selecting = "day";
      }
    }
  },
  computed: {
    displayText() {
      return this.date?.toLocaleString(this.time ? DateTime.DATETIME_MED : DateTime.DATE_MED);
    },
    weeks() {
      let day = this.date.startOf("month").startOf("week");
      let weekList = [];
      let list = [];
      let previousMonth = this.date.minus({ months: 1 }).startOf("month").month;
      let nextMonth = this.date.plus({ months: 1 }).startOf("month").month;
      while (day.month === previousMonth || day.month === this.date.month && day.day <= day.daysInMonth || day.month === nextMonth) {
        list.push(day);
        if (list.length === 7) {
          weekList.push(list);
          list = [];
        }
        if (day.day === day.daysInMonth && day.weekday === 7) break;
        if (day.month === nextMonth && day.weekday === 7) break;
        day = day.plus({ days: 1 });
      }
      return weekList;
    },
    weekDays() {
      return Info.weekdays("long", { locale: this.locale }).map(item => item.substring(0, 2));
    },
    months() {
      return Info.months("long", { locale: this.locale });
    },
    yearUnit() {
      return (1).toLocaleString(this.locale, { style: "unit", unit: "year", unitDisplay: "long" }).substring(2);
    },
  },
  methods: {
    monthSelected(month) {
      this.date = this.date.set({ month: month });
      this.selecting = "day";
    },
    yearSelected(year) {
      this.date = this.date.set({ year: year });
      this.selecting = "month";
    },
    dateSelected(date) {
      if (date.month === this.date.month) {
        this.active = false;
      }
      this.date = date;
    },
    checkValidity() {
      if (this.minDate && (this.date.diff(this.minDate) < 0)) this.date = this.minDate;
      if (this.maxDate && (this.date.diff(this.maxDate) > 0)) this.date = this.maxDate;
    },
    previousMonthClicked() {
      if (this.minDate) {
        this.date = DateTime.max(this.date.minus({ months: 1 }), this.minDate);
      } else {
        this.date = this.date.minus({ months: 1 });
      }
    },
    nextMonthClicked() {
      if (this.maxDate) {
        this.date = DateTime.min(this.date.plus({ months: 1 }), this.maxDate);
      } else {
        this.date = this.date.plus({ months: 1 });
      }
    }
  }
}
</script>